import { StylesGroup } from '../types';
import { getAllowedPropertiesForAction } from '../../../apis/services/editActionsServices/nativeActions/globalDesign/constants';
import { Meaningfulness } from '../../../apis/services/editActionsServices/nativeActions/globalDesign/common/meaningfulnessPredicates';
import { StylesDerivedNativeActions } from '@wix/editor-platform-host-integration-apis';
import {
  hasAnyMeaningfulValueCheckContext,
  isAllowedStylePresent,
} from '../../../apis/services/editActionsServices/nativeActions/globalDesign/common/actionValidators/actionVisibilityRules';
import { buildRootContextInfosMap } from '../common/grouping/helpers/buildRootContextInfosMap';
import { VirtualManifestAPI } from '@wix/editor-platform-manifest-services';
import { CSS_PROPERTIES } from '@wix/component-protocol/schema';

const checkGroupValues = (group: StylesGroup) => {
  return hasAnyMeaningfulValueCheckContext(
    group.context,
    new Set([CSS_PROPERTIES.CSS_PROPERTY_TYPE.strokeWidth]),
    Meaningfulness.meaningCheck(StylesDerivedNativeActions.strokeStyles),
    group.styleNamespace,
    new Set(group.items.map((item) => item.styleItemId)),
  );
};

/**
 * Stroke group display filter
 *
 * Filters component groups in the stroke panel based on stroke width values.
 *
 * Logic for each root component and its children:
 * 1. If ANY group (root or child) has stroke width > 0:
 *    - Show only groups that have stroke width > 0 (filters both roots and children)
 * 2. If NO group has stroke width > 0:
 *    - Show only root groups that are valid for stroke properties (have manifest support)
 *    - Hide child groups
 */
export const strokeGroupDisplayFilter = (
  groups: StylesGroup[],
  rootContexts: VirtualManifestAPI[],
): StylesGroup[] => {
  if (groups.length === 0) {
    return groups;
  }

  const result: StylesGroup[] = [];
  const rootContextInfos = buildRootContextInfosMap(groups, rootContexts);

  for (const rootContextInfo of Object.values(rootContextInfos)) {
    const contextGroups = [
      ...rootContextInfo.rootGroups,
      ...rootContextInfo.childGroups,
    ];

    const isSomeStrokeWidthVisible = contextGroups.some(checkGroupValues);

    if (isSomeStrokeWidthVisible) {
      result.push(...contextGroups.filter(checkGroupValues));
      continue;
    }

    for (const rootGroup of rootContextInfo.rootGroups) {
      if (
        isAllowedStylePresent(
          rootGroup.context,
          new Set(
            getAllowedPropertiesForAction(
              StylesDerivedNativeActions.strokeStyles,
            ),
          ),
          rootGroup.styleNamespace,
        )
      ) {
        result.push(rootGroup);
      }
    }
  }

  return result;
};
